import type { ErrorEvent } from '@sentry/nextjs';

import { isGoogleMapsInternalCrash } from './googleMapsNoise';
import { isThirdPartyScriptCrash } from './injectedScript';
import { REACT_HOOK_ORDER_FINGERPRINT, isReactHookOrderError } from './reactHookOrder';
import { XPCOM_EXCEPTION } from './xpcomException';

export const CLIENT_IGNORE_ERRORS: RegExp[] = [
  // Generic cross-origin errors
  /Script error/i,
  // WebKit module-script fetch aborts (Safari-only navigation/network noise)
  /Importing a module script failed/i,
  // Browser-extension noise: stackless runtime.sendMessage rejection denyUrls can't match
  /runtime\.sendMessage/i,
  // Wallet-extension inpage.js probes every page; frames normalize to app:/// so denyUrls miss them
  /Failed to connect to MetaMask/i,
  /MetaMask extension not found/i,
  // Office/Outlook injects a script that rejects with a stackless probe; denyUrls can't match it
  /Object Not Found Matching Id:\d+/i,
  // Firefox extensions throw XPCOM exceptions our boundaries re-wrap, losing the moz-extension
  // frame denyUrls needs
  XPCOM_EXCEPTION,
  // Google Maps JS API loader failures
  /Could not load "\w+"/i,
  /The Google Maps JavaScript API could not load/i,
];

export function isClientNoise(event: ErrorEvent): boolean {
  const primary = event.exception?.values?.at(-1);
  const message = primary?.value ?? '';
  const type = primary?.type ?? '';
  const mechanism = primary?.mechanism;
  const frames = primary?.stacktrace?.frames ?? [];
  const isSyntheticUnhandledRejection =
    mechanism?.type?.endsWith('onunhandledrejection') === true && mechanism.synthetic === true;

  if (
    isSyntheticUnhandledRejection &&
    (type === 'CustomEvent' ||
      type === 'Event' ||
      message === 'Object captured as promise rejection with keys: [object has no keys]')
  ) {
    return true;
  }

  const isTransientNetworkError =
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    message.includes('network error') ||
    message.includes('Network request failed') ||
    message.includes('Load failed') ||
    type === 'AbortError' ||
    message.includes('The user aborted a request') ||
    message.includes('The operation was aborted') ||
    message.includes('The request timed out') ||
    type === 'TimeoutError';

  return (
    isTransientNetworkError ||
    type === 'UnrecognizedActionError' ||
    isThirdPartyScriptCrash(mechanism, frames) ||
    isGoogleMapsInternalCrash(mechanism, frames)
  );
}

export function applyClientFingerprint(event: ErrorEvent): ErrorEvent {
  const message = event.exception?.values?.at(-1)?.value ?? '';

  if (isReactHookOrderError(message)) {
    event.fingerprint = [REACT_HOOK_ORDER_FINGERPRINT];
  }

  return event;
}
